// v3.15-1133 — Earnings Ledger (Phase 1)
//
// Per-user compensation dashboard. Non-admins see their own earnings only;
// admins see a global "All Earnings" tab + can record manual earnings and
// void existing rows.
//
// Tiles: MTD / YTD / Lifetime + the live current-year category breakdown.
// Transaction history with category + source + year filters.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DollarSign, Plus, X, Filter, Loader2, ShieldCheck, Sparkles, Lock, Trash2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import GhostModalHeader from '../components/GhostModalHeader';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const TileBig = ({ label, amount, subtitle, accent = 'blue', testId }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl bg-white p-5 shadow-sm border-2 ${
      accent === 'emerald' ? 'border-emerald-100' :
      accent === 'amber'   ? 'border-amber-100'   :
      accent === 'violet'  ? 'border-violet-100'  :
                             'border-blue-100'
    }`}
  >
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className={`text-3xl font-extrabold mt-1 ${
      accent === 'emerald' ? 'text-emerald-700' :
      accent === 'amber'   ? 'text-amber-700'   :
      accent === 'violet'  ? 'text-violet-700'  :
                             'text-blue-700'
    }`}>{fmtMoney(amount)}</div>
    {subtitle ? <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div> : null}
  </div>
);

const ManualEntryModal = ({ isOpen, onClose, onSaved, recipients }) => {
  const [recipientId, setRecipientId] = useState('');
  const [category, setCategory] = useState('cf360_commission');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [clientId, setClientId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRecipientId(''); setCategory('cf360_commission'); setAmount('');
      setNotes(''); setClientId('');
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!recipientId) { toast.error('Recipient required'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      const res = await api.post('/earnings/manual', {
        recipient_user_id: recipientId,
        client_id: clientId || null,
        category,
        amount: amt,
        notes,
      });
      toast.success(`Earning of ${fmtMoney(amt)} recorded`);
      onSaved?.(res.data?.item);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { id: 'cf360_commission',     label: 'CF360 Commission' },
    { id: 'concierge_commission', label: 'Concierge Commission' },
    { id: 'manager_override',     label: 'Manager Override' },
    { id: 'bonus',                label: 'Bonus' },
    { id: 'spiff',                label: 'Spiff' },
    { id: 'referral',             label: 'Referral' },
    { id: 'adjustment',           label: 'Adjustment' },
    { id: 'application_fee',      label: 'Application Fee (manual)' },
    { id: 'other',                label: 'Other' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="manual-earning-modal">
        <GhostModalHeader title="Add Manual Earning" subtitle="Legacy workflow — retired in v3.15-1217" icon={<Sparkles className="h-5 w-5" />} />
        {/* v3.15-1217 PAY-MOD-001 — Manual earning creation retired. The
            typed-amount origin has been replaced by consumption of
            authoritative V1 events (Compensation / Bonus / Spiff /
            Chargeback). See the audit report PAY-AUDIT-001 for context. */}
        <div className="p-5">
          <div
            className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2"
            data-testid="manual-earning-retired-banner"
          >
            <div className="font-semibold flex items-center gap-2">
              <span className="inline-flex h-2 w-2 rounded-full bg-amber-500" />
              This workflow has been retired
            </div>
            <p className="text-xs leading-relaxed">
              Manual earnings can no longer be created. Every earning in
              Prestigious Hub must originate from an authoritative Version 1
              event so it flows through the immutable pipeline:
              Compensation / Bonus / Spiff / Chargeback → Compensation Ledger
              → Money Manager → Financial Lifecycle → Payroll Transaction.
            </p>
            <p className="text-xs">
              <strong>Use instead:</strong>
            </p>
            <ul className="list-disc list-inside text-xs space-y-0.5 pl-2">
              <li>Bonuses → Bonus Engine (V1)</li>
              <li>Spiffs → Spiff Engine (V1)</li>
              <li>Commissions → Compensation Engine (V1)</li>
              <li>Chargebacks / Adjustments → Chargeback Engine (V1)</li>
            </ul>
            <p className="text-[10px] text-amber-800 italic">
              PAY-MOD-001 · v3.15-1217 · Legacy `POST /api/earnings/manual` returns HTTP 410 Gone.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="manual-earning-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function EarningsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [scope, setScope] = useState(() => searchParams.get('scope') === 'all' ? 'all' : 'mine');
  const [year, setYear] = useState('');         // '' = all-time
  const [category, setCategory] = useState(''); // '' = all
  const [source, setSource] = useState('');     // '' = all, 'system' or 'manual'
  const [recipients, setRecipients] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const isAdmin = useMemo(() => {
    const r = (user?.role || '').toLowerCase();
    return ['admin', 'manager', 'director', 'owner'].some((t) => r.includes(t));
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // v3.15-1218 PAY-MOD-002 — Migrated onto V1 authoritative pipeline.
      // Reads compensation_ledger via /legacy-reader adapter; totals come
      // from /legacy-reader/earnings/summary (mtd / ytd / lifetime + by-category).
      const listPath = scope === 'all' ? '/legacy-reader/earnings/all' : '/legacy-reader/earnings/mine';
      const listParams = { date_range: 'ytd' };
      const summaryParams = {};
      const [listRes, sumRes] = await Promise.all([
        api.get(listPath, { params: listParams }),
        api.get('/legacy-reader/earnings/summary', { params: summaryParams }),
      ]);
      let items = listRes.data?.items || [];
      // Client-side filters preserved for parity with the legacy UI.
      if (category) items = items.filter((it) => it.category === category);
      if (source)   items = items.filter((it) => it.source === source || it.system_source === source);
      const totals = sumRes.data?.totals || {};
      const byCatArr = sumRes.data?.by_category || [];
      const byCat = {};
      byCatArr.forEach((b) => { byCat[b.category] = b; });
      setData({
        items,
        totals,
        by_category: byCat,
        data_source: listRes.data?.data_source || 'v1_authoritative',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load earnings');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope, year, category, source]);

  useEffect(() => { load(); }, [load]);

  // Load recipient list for the manual modal (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/earnings/recipients');
        setRecipients(res.data?.users || []);
      } catch { /* noop */ }
    })();
  }, [isAdmin]);

  const handleVoid = async (_row) => {
    // v3.15-1218 PAY-MOD-002 — Legacy void endpoint was retired in PAY-MOD-001.
    // V1 handles corrections via compensating Compensation Events.
    toast.error('Voiding is retired. Issue a compensating Compensation Event instead.');
  };

  const handleBackfill = async () => {
    if (!window.confirm('Run a one-time backfill of EQF & Solar Exit application fees for ALL historical deals with vendor_payment_received_date? This is idempotent — re-running won\'t duplicate.')) return;
    setBackfilling(true);
    try {
      const res = await api.post('/earnings/backfill');
      const { scanned, written, skipped } = res.data || {};
      toast.success(`Backfill complete — scanned ${scanned}, wrote ${written}, skipped ${skipped}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const totals     = data?.totals || {};
  const items      = data?.items  || [];
  const byCat      = totals.by_category_ytd || {};
  const byYear     = totals.by_year || [];
  const categoryKeys = data?.categories ? Object.keys(data.categories) : [];

  return (
    <div className="space-y-4" data-testid="earnings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <DollarSign className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Earnings</h1>
            <p className="text-xs text-slate-500">v3.15-1133 · Earnings Ledger Phase 1 · Payroll remains untouched</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button
                size="sm" variant="outline"
                onClick={() => navigate('/earnings/reconciliation')}
                data-testid="open-reconciliation-btn"
              >
                <Scale className="h-4 w-4 mr-1.5" /> Reconciliation
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => setScope(scope === 'mine' ? 'all' : 'mine')}
                data-testid="scope-toggle-btn"
              >
                {scope === 'mine' ? 'View All Team Earnings' : 'View Only Mine'}
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={handleBackfill}
                disabled
                title="Retired in v3.15-1217 (PAY-MOD-001) — legacy earnings backfill has been retired"
                data-testid="backfill-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                Backfill App Fees (retired)
              </Button>
              <Button
                size="sm"
                onClick={() => setModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="add-manual-earning-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Manual Earning
              </Button>
            </>
          )}
        </div>
      </div>

      {/* v3.15-1218 PAY-MOD-002 — Reader migrated onto V1. */}
      <div
        className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2"
        data-testid="earnings-v1-source-banner"
      >
        <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">
            V1 Authoritative Pipeline
            <span className="text-[9px] uppercase tracking-wide bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full">
              live
            </span>
          </div>
          <div className="mt-0.5 leading-relaxed">
            Earnings are sourced from <code>compensation_ledger</code> (Bonus / Spiff / Compensation /
            Chargeback engines · MM-002 lifecycle) via the V1 reader adapter. Legacy manual entry
            remains retired.
          </div>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <TileBig label="Month to Date" amount={totals.mtd} subtitle={totals.current_month} accent="emerald" testId="tile-mtd" />
        <TileBig label="Year to Date"  amount={totals.ytd} subtitle={`Calendar ${totals.current_year || ''}`} accent="blue" testId="tile-ytd" />
        <TileBig label="Lifetime"      amount={totals.lifetime} subtitle="All time" accent="violet" testId="tile-lifetime" />
        <TileBig
          label="Categories"
          amount={Object.values(byCat).reduce((a, b) => a + (b.total || 0), 0)}
          subtitle={`${Object.keys(byCat).length} active YTD`}
          accent="amber"
          testId="tile-categories"
        />
      </div>

      {/* Category breakdown YTD */}
      {Object.keys(byCat).length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="category-breakdown">
          <div className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" /> Category Breakdown — YTD
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(byCat).map(([k, v]) => (
              <button
                key={k}
                type="button"
                onClick={() => setCategory(category === k ? '' : k)}
                className={`text-left rounded-xl px-3 py-2 border transition-colors ${
                  category === k ? 'bg-blue-50 border-blue-300' : 'border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`category-pill-${k}`}
              >
                <div className="text-[10px] uppercase font-bold text-slate-500">{v.label}</div>
                <div className="text-lg font-bold text-slate-900">{fmtMoney(v.total)}</div>
                <div className="text-[11px] text-slate-500">{v.count} entries</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-wrap items-center gap-3" data-testid="filter-bar">
        <Select value={year || 'all'} onValueChange={(v) => setYear(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="year-filter"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            {(byYear.length > 0 ? byYear : [{ year: String(new Date().getFullYear()) }]).map((y) => (
              <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categoryKeys.map((k) => (
              <SelectItem key={k} value={k}>{data?.categories?.[k] || k}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source || 'all'} onValueChange={(v) => setSource(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="source-filter"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="system">System Generated</SelectItem>
            <SelectItem value="manual">Manual Entry</SelectItem>
          </SelectContent>
        </Select>
        {(category || source || year) && (
          <Button variant="ghost" size="sm" onClick={() => { setCategory(''); setSource(''); setYear(''); }} data-testid="clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {/* Transaction list */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="earnings-table">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900">Transaction History</div>
          <div className="text-xs text-slate-500">{items.length} {items.length === 1 ? 'entry' : 'entries'}</div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
            <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm" data-testid="earnings-empty">
            No earnings recorded for the selected filters.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((row) => (
              <div
                key={row.id}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 ${row.voided ? 'opacity-50 line-through' : ''}`}
                data-testid={`earning-row-${row.id}`}
              >
                <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                  row.source === 'system' ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-600'
                }`}>
                  {row.source === 'system' ? <Lock className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{row.category_label}</span>
                    {row.source === 'system' && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">SYSTEM</span>
                    )}
                    {row.backfilled && (
                      <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">BACKFILL</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {row.recipient_name && scope === 'all' ? <>→ <strong>{row.recipient_name}</strong> · </> : null}
                    {row.client_name ? <>{row.client_name} · </> : null}
                    {fmtDate(row.earned_date)}
                    {row.notes ? ` · ${row.notes}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold text-slate-900">{fmtMoney(row.amount)}</div>
                  <div className="text-[10px] text-slate-500">by {row.created_by_name || 'Hub'}</div>
                </div>
                {isAdmin && !row.voided && (
                  <button
                    type="button"
                    onClick={() => handleVoid(row)}
                    disabled={voiding === row.id}
                    className="ml-2 p-1.5 rounded hover:bg-red-50 text-rose-600 disabled:opacity-50"
                    data-testid={`void-btn-${row.id}`}
                    title="Void earning"
                  >
                    {voiding === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ManualEntryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        recipients={recipients}
      />
    </div>
  );
}
